import axios from 'axios';
import moment from 'moment';
import queryParams from './utils/queryParams';

const sendAuthorizationEmail = (orderId, orderType, email, employerName, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.post(`/v1/authorizations/${orderId}/email`, {orderType, email, employerName}, { signal })
    .then(response => {
      resolve(response.data);
    })
    .catch(err => reject(err));
  });
};

const getOpenOrders = (selectedSites, forEmployee, onlyPhysicals, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/orders?sites=${selectedSites}&openOrders=true${forEmployee}${onlyPhysicals ? '&onlyPhysicals' : ''}`, {signal})
    .then(response => {
      return resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error('Failed to get employee open orders', err);
      reject(err);
    });
  });
};

const getPreviousOrders = (selectedSites, forEmployee, startDate, endDate, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/orders?sites=${selectedSites}${forEmployee ? `&forEmployee=${forEmployee}` : `&startDate=${startDate}&endDate=${endDate}`}`, {signal})
    .then(response => {
      return resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error('Failed to get previous orders', err);
      reject(err);
    });
  });
};

/**
 * Get drug tests for an employee from IMS
 * @param {string} legacyEmployeeId - The employee's legacy ID from MongoDB the corresponds to the IMS Utility API ID
 * @param {Object} signal - the cancel signal sent to Axios
 */
const getDrugTestsForEmployee = async (legacyEmployeeId, signal = null) => {
  try {
    const drugTests = await axios.get(`/v1/orders/getDrugTestsForEmployee/${legacyEmployeeId}`, { signal });
    return drugTests;
  } catch (err) {
    console.error('Failed to get drug tests for employee', err);
    throw err;
  }
};

/**
 * Reopens drug order in IMS
 * @param {String} orderId - the order ID from IMS
 * @param {Object} signal - the cancel signal sent to Axios
 * @returns 
 */
const reopenOrder = async (orderId, signal = null) => {
  try {
    const reopenedOrder = await axios.patch(`/v1/orders/${orderId}/reopen`, { signal });
    return reopenedOrder;
  } catch (err) {
    console.error('Failed to reopen order', err);
    throw err;
  }
}


const searchOrders = (queryString, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.get(`/v1/orders?${queryString}`, {signal})
    .then(response => {
      if (!response.data.hasOwnProperty("orderResults")) {
        return resolve(response.data);
      }
      return resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error('Failed to get orders', err);
      reject(err);
    });
  });
};


const updateOrder = (orderId, orderStatusChanges, signal = null) => {
  return new Promise((resolve, reject) => {
    axios
    .patch(`/v1/orders/${orderId}`, orderStatusChanges, {signal})
    .then(response => {
      return resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error('Failed to update order', err);
      reject(err);
    });
  });
};

const cancelOrder = (order, email, signal = null) => {
  return new Promise((resolve, reject) => {
    const orderUpdates = {
      "orderId": order.orderId,
      "medicalExamOrderId": order.orderId,
      "userEmail": email,
      "orderType": order.orderType,
      "updateLog": {
        "updateStatus": {
          "orderStatus": "Canceled",
          "orderStatusId": 6,
          "auditMessage": "Status changed to Canceled"
        }
      },
      "orderSource": order.orderSource
    };
    axios
    .patch(`/v1/orders/${order.orderId}`, orderUpdates, { signal})
    .then(response => {
      return resolve(response.data);
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return reject(err.message);
      }
      console.error('Failed to update order', err);
      reject(err);
    });
  });
};

const getAuditTrail = (orderId, signal = null) => {
  return new Promise((resolve, reject) => {
    if (!orderId) {
      return resolve([]);
    }
    axios.get(`/v1/orders/${orderId}/auditTrail`, {signal})
    .then(response => {
      let auditTrail = response.data;
      for (let i = 0; i < auditTrail.length; i++) {
        auditTrail[i].AuditTime = moment(auditTrail[i].AuditTime).format('M/D/YYYY h:mm a')
      }
      return resolve(auditTrail);
    })
    .catch(err => {
      if (!axios.isCancel(err)) {
        return reject(err);
      }
    });
  });
};

const addAuditTrail = (orderId, message, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.post(`/v1/orders/${orderId}/auditTrail`, {message}, {signal})
    .then(response => {
      return resolve();
    })
    .catch(err => {
      return reject(err);
    });
  });
};

const refreshMVR = (MVRId, signal = null) => {
  return new Promise((resolve, reject) => {
    axios.patch(`/v1/orders/${MVRId}/refreshmvr`, {}, {signal})
    .then(response => resolve())
    .catch(reject)
  });
};



// CME = Certified Medical Exam
const CME = {
  /**
   * Get CME (Certified Medical Exam) orders
   * @param {*} queryString 
   * @param {*} signal 
   * @returns 
   */
  getOrders (queryString, signal = null) {
    return new Promise((resolve, reject) => {
      
      const url = `/v1/orders/cme?${queryString}`;

      axios.get(url, {signal})
      .then(response => {
        return resolve(response.data);
      })
      .catch(err => {
        if (axios.isCancel(err)) {
          return reject(err.message);
        }
        console.error('Failed to get orders', err);
        reject(err);
      });
    });
  }
}

// EPN = Employer Pull Notice
/**
 * @typedef {Object} EpnSite
 * @property {number} id - Site ID
 * @property {number} employerId - Employer ID
 * @property {string} employerName - Employer name
 * @property {string} employerCity - Employer city
 * @property {string} employerState - Employer state
 * @property {string} employerZip - Employer zip code
 * @property {string} requestorCode - Requestor code
 * @property {string} requestorName - Requestor name
 * @property {string} requestorAttn - Attention line
 * @property {boolean} duplicateItems - Whether duplicate items exist
 * @property {number} billingPrice - Billing price
 * @property {string} requesterAddressLine1 - Address line 1
 * @property {string} requesterAddressLine2 - Address line 2
 * @property {string} notificationRecipients - Email recipients for notifications
 * @property {string|null} reason - Reason field
 * @property {boolean} isEpnActive - Whether EPN is active
 * @property {boolean} employerActive - Whether employer is active
 * @property {string|null} dotNum - DOT number
 */

/**
 * @typedef {Object} EpnEligibleLocation
 * @property {number} erId - Employer ID
 * @property {string} name - Location name
 * @property {string} employerCode - Employer code
 * @property {string} city - City
 * @property {string} state - State
 * @property {string|null} dotNum - DOT number
 */

/**
 * @typedef {Object} EpnSiteCreationPayload
 * @property {number} employerId - Employer ID
 * @property {string} requestorCode - Requestor code
 * @property {string} name - Site name
 * @property {string} attn - Attention name
 * @property {string} street - Street address
 * @property {string} city - City
 * @property {string} state - State
 * @property {string} zip - Zip code
 * @property {string} [recipients] - Email recipients (optional)
 */

const EPN = {
  /**
   * Get EPN Driver's License inquiry requests for a specific employee
   * @param {Object} params - Parameters object
   * @param {number} params.emid - Employee ID (required)
   * @param {boolean|null} [params.isCompleted=null] - Completion status filter (optional)
   * @param {Object} [params.signal] - The cancel signal sent to Axios (optional)
   * @returns {Promise<Array>} Array of EPN DL inquiry requests
   */
  async getDlInquiryRequestsByEmid({ emid, isCompleted = null, signal = null }) {
    if (!emid) {
      throw new Error('emid is required');
    }
    let url = `/v1/orders/epn/employees/${emid}/dlInquiryRequests`;
    if (isCompleted !== null && isCompleted !== undefined) {
      url += `?isCompleted=${isCompleted}`;
    }
    try {
      const response = await axios.get(url, { signal });
      return response.data;
    } catch (err) {
      if (axios.isCancel(err)) {
        throw new Error(err.message);
      }
      console.error('Failed to get EPN DL inquiry requests by EMID', err);
      throw err;
    }
  },

  /**
   * Batch insert EPN Driver's License inquiry requests
   * @param {number} userId - User ID of the requester
   * @param {Array<Object>} orders - Array of order objects with employeeId and employerId
   * @param {number} orders[].employeeId - Employee ID (legacy EMID)
   * @param {number} orders[].employerId - Employer/site ID (legacy ERID)
   * @param {Object} signal - The cancel signal sent to Axios
   * @returns {Promise<Object>} Result object with data and status properties
   */
  async createDLInquiryRequests(userId, orders, signal = null) {
    try {
      const response = await axios.post('/v1/orders/epn/dlInquiryRequests', { userId, orders }, { signal });
      return { data: response.data, status: response.status };
    } catch (err) {
      if (axios.isCancel(err)) {
        throw new Error(err.message);
      }
      console.error('Failed to create EPN DL inquiry requests', err);
      throw err;
    }
  },

   /**
   * Get EPN sites by L1 code with optional active status filtering
   * @param {Object} params - Parameters object
   * @param {number} params.l1Code - The L1 code (database primary key ID) used to filter sites
   * @param {boolean|null} [params.isActiveOnly] - Filter by active status (optional: true/false/null for all)
   * @param {Object} [params.signal] - The cancel signal sent to Axios
   * @returns {Promise<EpnSite[]>} Array of EPN sites
   */
  async getSitesByL1Code({ l1Code, isActiveOnly = null, signal = null }) {
    try {
      const params = new URLSearchParams({ l1Code });
      // Only add isActive parameter if it's explicitly provided (not null)
      if (isActiveOnly !== null) {
        params.append('isActive', isActiveOnly);
      }
      const url = `/v1/orders/epn/sites?${params.toString()}`;
      
      const response = await axios.get(url, { signal });
      return response.data;
    } catch (err) {
      if (axios.isCancel(err)) {
        throw new Error(err.message);
      }
      console.error('Failed to get EPN sites by L1 code', err);
      throw err;
    }
  },

  /**
   * Get eligible locations for EPN site creation by L1 code
   * @param {Object} params - Parameters object
   * @param {number} params.l1Code - The L1 code used to filter eligible locations
   * @param {Object} [params.signal] - The cancel signal sent to Axios
   * @returns {Promise<EpnEligibleLocation[]>} Array of eligible locations
   */
  async getEligibleLocations({ l1Code, signal = null }) {
    try {
      const params = new URLSearchParams({ l1Code });
      const response = await axios.get(`/v1/orders/epn/eligibleLocations?${params.toString()}`, { signal });
      return response.data;
    } catch (err) {
      if (axios.isCancel(err)) {
        throw new Error(err.message);
      }
      console.error('Failed to get EPN eligible locations', err);
      throw err;
    }
  },

  /**
   * Batch create EPN sites
   * @param {Object} params - Parameters object
   * @param {EpnSiteCreationPayload[]} params.sites - Array of site objects to create
   * @param {Object} [params.signal] - The cancel signal sent to Axios
   * @returns {Promise<Object>} Result from Concorde API
   */
  async createSitesBatch({ sites, signal = null }) {
    try {
      const response = await axios.post('/v1/orders/epn/batchSites', { sites }, { signal });
      return response.data;
    } catch (err) {
      if (axios.isCancel(err)) {
        throw new Error(err.message);
      }
      console.error('Failed to create EPN sites batch', err);
      throw err;
    }
  },

  /**
   * Deactivate an EPN site
   * @param {Object} params - Parameters object
   * @param {number} params.employerId - The employer ID of the site to deactivate
   * @param {string} params.deactivationReason - Reason for deactivation (max 1000 chars)
   * @param {Object} [params.signal] - The cancel signal sent to Axios
   * @returns {Promise<Object>} Result from API
   */
  async deactivateSite({ employerId, deactivationReason, signal = null }) {
    try {
      const response = await axios.delete('/v1/orders/epn/site', { 
        data: { employerId, deactivationReason },
        signal 
      });
      return response.data;
    } catch (err) {
      if (axios.isCancel(err)) {
        throw new Error(err.message);
      }
      console.error('Failed to deactivate EPN site', err);
      throw err;
    }
  },

  /**
   * Get EPN Driver's License Inquiry Status List (DLIST)
   * @param {Object} params - Parameters object
   * @param {string} params.requestorCode - The requestor code to filter DLIST records
   * @param {string} params.date - The date to filter DLIST records in YYYY-MM-DD format
   * @param {Object} [params.signal] - The cancel signal sent to Axios
   * @returns {Promise<Object>} DLIST data with header and driver records
   */
  async getDlist({ requestorCode, date, signal = null }) {
    try {
      const params = new URLSearchParams({ requestorCode, date });
      const url = `/v1/orders/epn/dlist?${params.toString()}`;
      
      const response = await axios.get(url, { signal });
      return response.data;
    } catch (err) {
      if (axios.isCancel(err)) {
        throw new Error(err.message);
      }
      console.error('Failed to get EPN DLIST', err);
      
      // Extract error message from response if available (e.g., 404 errors)
      if (err.response?.data?.message) {
        throw new Error(err.response.data.message);
      }
      
      throw err;
    }
  },

  /**
   * Get EPN ADC orders by employer and optional filters.
   * @param {Object} params - Parameters object
   * @param {number|string} params.employerId - Employer ID (required)
   * @param {boolean|null} [params.includeChildren=null] - Include child employers (optional)
   * @param {boolean|null} [params.isCompleted=null] - Completion status filter (optional)
   * @param {string|null} [params.startDate=null] - Start date filter (optional)
   * @param {string|null} [params.endDate=null] - End date filter (optional)
   * @param {Object} [params.signal] - The cancel signal sent to Axios
   * @returns {Promise<Object>} ADC orders payload
   */
  async getAdcOrders({ 
    employerId, 
    includeChildren = null, 
    isCompleted = null, 
    startDate = null, 
    endDate = null, 
    signal = null 
  }) {
    queryParams.requireValue(employerId, 'employerId');

    try {
      const params = new URLSearchParams({ employerId: String(employerId) });
      queryParams.appendParamIfPresent(params, 'includeChildren', includeChildren, value => String(value));
      queryParams.appendParamIfPresent(params, 'isCompleted', isCompleted, value => String(value));
      queryParams.appendParamIfPresent(params, 'startDate', startDate);
      queryParams.appendParamIfPresent(params, 'endDate', endDate);

      const url = `/v1/orders/epn/adcOrders?${params.toString()}`;
      const response = await axios.get(url, { signal });
      return response.data;
    } catch (err) {
      if (axios.isCancel(err)) {
        throw new Error(err.message);
      }
      console.error('Failed to get EPN ADC orders', err);
      throw err;
    }
  }
}


export default {
  sendAuthorizationEmail,
  getOpenOrders,
  updateOrder,
  cancelOrder,
  getPreviousOrders,
  getDrugTestsForEmployee,
  reopenOrder,
  searchOrders,
  getAuditTrail,
  addAuditTrail,
  refreshMVR,
  CME,
  EPN
};